import { useCurrentCompany, useCurrentUser } from '@bonusly/who-am-i';

import { useManagerCheckInSettings } from './use-manager-check-in-settings';

/**
 * Hook to determine if check-ins are configured for the current user/company.
 * Check-ins are considered configured when:
 * 1. The company has mandatory check-ins enabled (and past the effective date), OR
 * 2. A cadence is set (either on the current user's manager OR the current user themselves, depending on forManager param)
 *
 * @param asManager - When true, checks the current user's own settings (because the user is a manager).
 *                     When false (default), checks the current user's manager's settings (use for direct reports views).
 * @returns boolean - true if check-ins are configured, false otherwise
 */
export const useCheckInsConfigured = (asManager = false): boolean => {
	const company = useCurrentCompany();
	const currentUser = useCurrentUser();
	const { data: managerCheckInSettings } = useManagerCheckInSettings();

	// Check if company has mandatory check-ins enabled
	const companyCheckInSettings = company?.check_in_settings;
	const isCompanyMandatory =
		companyCheckInSettings?.mandatory && (!companyCheckInSettings?.effective_date || new Date() >= new Date(companyCheckInSettings.effective_date));

	// Check if a cadence is set
	// For manager views: check current user's own settings
	// For IC views: check current user's manager's settings
	const hasCadence = asManager ? !!currentUser?.manager_check_in_settings?.cadence : !!managerCheckInSettings?.cadence;

	return isCompanyMandatory || hasCadence;
};
